import { format } from 'date-fns';
import {
  Activity,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Hash,
  RefreshCw,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useGetProofreadingJobQuery } from '@/services/api/proofreadingService';
import {
  ProofreadingRuleResult,
  ProofreadingRuleResultStatus,
} from '@/services/types/proofreading-types';

import { ProofreadingRuleResultCard } from './ProofreadingRuleResultCard';

interface ProofreadingJobDetailProps {
  jobId: string;
  workspaceId: string;
  matterId: string;
  onClose: () => void;
}

// Severity ranking for rule results: lower number indicates higher severity
const SEVERITY_RANK: Record<ProofreadingRuleResultStatus, number> = {
  [ProofreadingRuleResultStatus.CRITICAL_ISSUES_FOUND]: 0,
  [ProofreadingRuleResultStatus.MODERATE_ISSUES_FOUND]: 1,
  [ProofreadingRuleResultStatus.MINOR_ISSUES_FOUND]: 2,
  [ProofreadingRuleResultStatus.UNABLE_TO_ASSESS]: 3,
  [ProofreadingRuleResultStatus.NO_ISSUES_FOUND]: 4,
  [ProofreadingRuleResultStatus.PROCESSING]: 5,
  [ProofreadingRuleResultStatus.FAILED]: 6,
};

// Priority rules to show by default - matches by title substring (case-insensitive)
const PRIORITY_RULE_PATTERNS = [
  // CPT Coding Accuracy
  'accurate cpt codes',
  // Provider Accuracy
  'verify all providers',
  // Medical Bill Accuracy
  'billing accuracy',
  // Statutory Check
  'cite specific statutes',
  'jurisdiction-specific law',
  // Pain and Suffering Narrative
  'rooted in records',
  'customized narrative',
  'quantification methods',
  'focus on human impact',
  // Recipient Accuracy
  'recipient accuracy',
  // Diagnosis Accuracy
  'no fabricated diagnoses',
  'comprehensive inclusion',
];

/**
 * Check if a rule title matches any priority pattern
 */
const isPriorityRule = (title: string): boolean => {
  const lowerTitle = title.toLowerCase();
  return PRIORITY_RULE_PATTERNS.some((pattern) =>
    lowerTitle.includes(pattern.toLowerCase()),
  );
};

/**
 * Sort results by severity (most severe first)
 */
const sortBySeverity = (
  results: ProofreadingRuleResult[],
): ProofreadingRuleResult[] =>
  [...results].sort(
    (a, b) => SEVERITY_RANK[a.status] - SEVERITY_RANK[b.status],
  );

export const ProofreadingJobDetail: React.FC<ProofreadingJobDetailProps> = ({
  jobId,
  workspaceId,
  matterId,
  onClose,
}) => {
  const [showOtherRules, setShowOtherRules] = useState(false);

  const { data: job, isFetching } = useGetProofreadingJobQuery(
    { matterId, workspaceId, jobId },
    {
      pollingInterval: 5000,
    },
  );

  // Split results into priority and other rules
  const { priorityResults, otherResults } = useMemo(() => {
    if (!job?.results) return { priorityResults: [], otherResults: [] };

    const priority = job.results.filter((result) =>
      isPriorityRule(result.title),
    );
    const other = job.results.filter((result) => !isPriorityRule(result.title));

    return {
      priorityResults: sortBySeverity(priority),
      otherResults: sortBySeverity(other),
    };
  }, [job?.results]);

  if (!job) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      <div>
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Analysis Results
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {isFetching && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Updating...
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Close details"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Job Metadata */}
        <div className="p-6 border-b border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Status
                </p>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${getStatusColor(job.status)}`}
                >
                  {job.status}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Created
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {format(new Date(job.created_at), 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-gray-500">
                  {format(new Date(job.created_at), 'h:mm a')}
                </p>
              </div>
            </div>

            {job.completed_at && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Completed
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {format(new Date(job.completed_at), 'MMM d, yyyy')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(job.completed_at), 'h:mm a')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Hash className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Version
                </p>
                <p className="text-sm font-medium text-gray-900">
                  v{job.version}
                </p>
                <p className="text-xs text-gray-500">
                  {job.results.length} rules
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-4">
          {job.results.length > 0 ? (
            <div className="space-y-4">
              {/* Priority Rules - Always Visible */}
              {priorityResults.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Key Checks ({priorityResults.length})
                  </h3>
                  {priorityResults.map((result) => (
                    <ProofreadingRuleResultCard
                      key={result.id}
                      result={result}
                      matterId={matterId}
                      workspaceId={workspaceId}
                      jobId={job.id}
                    />
                  ))}
                </div>
              )}

              {/* Other Rules - Collapsible */}
              {otherResults.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowOtherRules((v) => !v)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors w-full"
                  >
                    {showOtherRules ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span>
                      {showOtherRules ? 'Hide' : 'Show'} Additional Checks (
                      {otherResults.length})
                    </span>
                  </button>

                  {showOtherRules && (
                    <div className="mt-3 space-y-3">
                      {otherResults.map((result) => (
                        <ProofreadingRuleResultCard
                          key={result.id}
                          result={result}
                          matterId={matterId}
                          workspaceId={workspaceId}
                          jobId={job.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Edge case: no priority rules matched, show all */}
              {priorityResults.length === 0 && otherResults.length === 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Analysis Results ({job.results.length})
                  </h3>
                  {sortBySeverity(job.results).map((result) => (
                    <ProofreadingRuleResultCard
                      key={result.id}
                      result={result}
                      matterId={matterId}
                      workspaceId={workspaceId}
                      jobId={job.id}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">
                No results yet
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Analysis is still in progress. Results will appear here as they
                become available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
